import useProfiler from "hooks/useProfiler";
import { createTrackingUtils } from "..";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { useCallback, useMemo } from "react";
import useGlobalContext from "hooks/useGlobalContext";

const utils = createTrackingUtils({
  name: "flights_split_bounds_us",
  stages: {
    main: 1,
    desktop: 2,
    mobile: 3,
    direct: 4,
    meta: 5,
    domestic: 6,
    international: 7,
    genius_badge_visible: 8,
    redirected_to_outbound_from_inbound: 9
  },
  defaultStage: 1,
  goals: {
    cta_click: 1,
    change_depart_flight_click: 2,
    negative_incremental_price_visible: 3,
    negative_incremental_price_pay_now_click: 4,
    genius_offer_pay_now_click: 5
  }
});

export const useShouldTrackSplitBounds = () => {
  const { isAmerican, isRountrip } = useProfiler();
  const { isSplitBoundsAvailable } = useGlobalContext();

  return useMemo(() => {
    if (!isAmerican() || !isRountrip()) return false;
    return isAmerican() && isRountrip() && isSplitBoundsAvailable;
  }, [isAmerican, isRountrip, isSplitBoundsAvailable]);
};

export const useTrackSplitBoundsRedirect = () => {
  const { isAmerican, isRountrip } = useProfiler();
  const { isSplitBoundsAvailable } = useGlobalContext();

  return useCallback(
    () => isAmerican() && isRountrip() && isSplitBoundsAvailable && utils.stages.redirected_to_outbound_from_inbound(),
    [isAmerican, isRountrip, isSplitBoundsAvailable]
  );
};

export const useTrackSplitBoundsGeniusBadge = () => {
  const { isAmerican, isRountrip } = useProfiler();

  return useCallback(
    () => isAmerican() && isRountrip() && utils.stages.genius_badge_visible(),
    [isAmerican, isRountrip]
  );
};

export const useTrackSplitBoundsStages = () => {
  const { isAmerican, isRountrip, isDesktop, isMobile, isDirect, isMeta, isInternational, isDomestic } = useProfiler();
  const { stages } = utils;

  if (isAmerican() && isRountrip()) {
    stages.main();
    isDesktop() && stages.desktop();
    isMobile() && stages.mobile();
    isDirect() && stages.direct();
    isMeta() && stages.meta();
    isInternational() && stages.international();
    isDomestic() && stages.domestic();
  }
};

const key = "split_bounds_tracking";

const getTrackingData = (): Record<string, { price?: true; genius?: true }> => flightsSessionStore.get(key) || {};

const addTokenTrackingValue = (token: string, type: "price" | "genius") => {
  const data = getTrackingData();
  const tokenData = data[token] || {};

  flightsSessionStore.set(
    key,
    {
      ...data,
      [token]: { ...tokenData, [type]: true }
    },
    { ttl: "5m" }
  );
};
const getTokenTrackingValue = (token: string): { price?: true; genius?: true } | undefined => {
  const data = getTrackingData();
  return data[token];
};

export const splitBoundsTrackingStorage = {
  addValue: addTokenTrackingValue,
  getValue: getTokenTrackingValue
};

export default utils;
